import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ContratoFila, ContratosTablero } from "../../../../services/v2/enrollmentRequests.service";
import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import useEsTelefono from "../../../../utils/esTelefono";
import "./contratosFirma.css";

/*
	Matrículas → Contratos (Esteban, 2026-10-15: "llevar un trackeo exacto de contratos pendientes; cuando se vaya firmando
	deberíamos ir viéndolo, y el no firmado también; ahorita este control se lleva manual"). Cada contrato con su estado:
	sin enviar, enviado por WhatsApp, abierto por el cliente, firmado. Desde aquí se manda o reenvía el enlace y se copia.
*/

const FILTROS: { key: string; label: string }[] = [
	{ key: "pendientes", label: "Pendientes" }, { key: "sin_enviar", label: "Sin Enviar" }, { key: "enviado", label: "Enviados" },
	{ key: "visto", label: "Abiertos" }, { key: "firmados", label: "Firmados" }, { key: "revisar", label: "Papel Por Revisar" },
	{ key: "error", label: "No Se Pudo Enviar" }, { key: "pago_pendiente", label: "Pago Por Procesar" }, { key: "", label: "Todos" },
];
const PENDIENTES = ["sin_enviar", "enviado", "visto", "error", "pago_pendiente", "generando", "revisar"];

const errorTexto = (e: any): string => e?.response?.data?.message || e?.response?.data?.error || "Algo falló. Intenta otra vez.";
/** "15 oct 2:24 pm" en hora del centro (México y Honduras, UTC-6). */
const cuando = (iso: string | null): string => {
	if (!iso) return "";
	const d = new Date(new Date(iso).getTime() - 6 * 3600000);
	const mes = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"][d.getUTCMonth()];
	const h = d.getUTCHours();
	return d.getUTCDate() + " " + mes + " " + ((h % 12) || 12) + ":" + String(d.getUTCMinutes()).padStart(2, "0") + " " + (h < 12 ? "am" : "pm");
};
const dia = (ymd: string): string => {
	const [y, m, d] = ymd.split("-").map(Number);
	return d + " " + ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"][m - 1] + (y !== new Date().getFullYear() ? " " + y : "");
};
/** Copia al portapapeles; sin permiso (o fuera de HTTPS) prueba a la antigua. false = no se pudo. */
const copiarTexto = async (t: string): Promise<boolean> => {
	try {
		await navigator.clipboard.writeText(t);
		return true;
	} catch (e) {
		const area = document.createElement("textarea");
		area.value = t;
		area.style.position = "fixed";
		area.style.opacity = "0";
		document.body.appendChild(area);
		area.select();
		let ok = false;
		try { ok = document.execCommand("copy"); } catch (x) { ok = false; }
		document.body.removeChild(area);
		return ok;
	}
};
const tono = (estado: string): string =>
	estado === "firmado" || estado === "firmado_papel" ? "firmado" : estado === "error" || estado === "pago_pendiente" || estado === "revisar" ? "oro" : estado === "visto" ? "visto" : "";
const esFirmado = (estado: string) => estado === "firmado" || estado === "firmado_papel" || estado === "revisar";

/** La línea de detalle debajo del estado: cuándo se mandó, si lo abrió, quién firmó. */
const detalle = (c: ContratoFila): string => {
	if (c.estado === "firmado") return [(c.firmante || "") + (c.relacion === "tutor" ? " (tutor)" : ""), cuando(c.firmadoEl),
		c.metodo === "acepto" ? "aceptó sin firma" : "firma en celular", c.dispositivo].filter(Boolean).join(" · ");
	if (c.estado === "firmado_papel" || c.estado === "revisar") return ["Papel" + (c.subio ? " subido por " + c.subio : ""), cuando(c.firmadoEl),
		c.iaNombre ? "IA: " + c.iaNombre + (c.iaNota ? " — " + c.iaNota : "") : null].filter(Boolean).join(" · ");
	if (c.estado === "visto") return "Lo abrió " + cuando(c.abiertoEl) + (c.vecesAbierto > 1 ? " · " + c.vecesAbierto + " veces" : "");
	if (c.estado === "enviado") return "Enviado " + cuando(c.enviadoEl) + (c.modo_envio === "plantilla_sin_link" ? " · sin enlace" : "");
	if (c.estado === "error") return c.error || "";
	if (c.estado === "pago_pendiente") return "Se manda solo cuando Caja procese el pago";
	return "";
};

const ContratosFirma: React.FC<{ campusId: number }> = ({ campusId }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [estado, setEstado] = useState<string>("pendientes");
	const [q, setQ] = useState("");
	const [buscar, setBuscar] = useState("");
	const [desde, setDesde] = useState("");
	const [hasta, setHasta] = useState("");
	const [modo, setModo] = useState<number | "">("");
	const [vendio, setVendio] = useState<number | "">("");
	const [page, setPage] = useState(1);
	const [datos, setDatos] = useState<ContratosTablero | null>(null);
	const [cargando, setCargando] = useState(false);
	const [aviso, setAviso] = useState<{ t: string; oro?: boolean; enlace?: string } | null>(null);
	const [ocupado, setOcupado] = useState<number | null>(null);
	const perPage = 50;

	const cargar = useCallback(async () => {
		setCargando(true);
		try {
			const r = await http.enrollmentRequestsService.contratos(campusId, { estado: estado || undefined, q: buscar || undefined, desde: desde || undefined,
				hasta: hasta || undefined, modo: modo || undefined, vendio: vendio || undefined }, perPage, page);
			setDatos(r.data);
		} catch (e) {
			setAviso({ t: errorTexto(e), oro: true });
		} finally {
			setCargando(false);
		}
	}, [campusId, estado, buscar, desde, hasta, modo, vendio, page]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => { cargar(); }, [cargar]);
	// Tras subir un papel se vuelve a leer cuando la IA ya lo revisó: con los filtros de ese momento y sin quedar suelto al salir.
	const cargarRef = useRef(cargar);
	cargarRef.current = cargar;
	const reloj = useRef<number | null>(null);
	useEffect(() => () => { if (reloj.current) window.clearTimeout(reloj.current); }, []);

	const avisar = (t: string, oro = false) => { setAviso({ t, oro }); window.setTimeout(() => setAviso(null), 6000); };

	const enviar = async (c: ContratoFila) => {
		setOcupado(c.id);
		try {
			const r = await http.enrollmentRequestsService.enviarContratoFirma(campusId, c.id);
			avisar(r.data.modo === "plantilla_sin_link" ? "Se mandó el contrato, pero sin enlace: falta que Meta apruebe la plantilla de firma. Copia el enlace y mándalo cuando te conteste."
				: "Contrato enviado a " + c.alumno + " por WhatsApp.");
			cargar();
		} catch (e) {
			avisar(errorTexto(e), true);
			cargar();
		} finally {
			setOcupado(null);
		}
	};

	const copiar = async (c: ContratoFila) => {
		setOcupado(c.id);
		try {
			const enlace = c.enlace || (await http.enrollmentRequestsService.enlaceContratoFirma(campusId, c.id)).data.enlace;
			if (await copiarTexto(enlace)) avisar("Enlace copiado. Pégalo en el WhatsApp del cliente.");
			else setAviso({ t: "Copia este enlace y pégalo en el WhatsApp del cliente:", enlace });
			if (!c.enlace) cargar();
		} catch (e) {
			avisar(errorTexto(e), true);
		} finally {
			setOcupado(null);
		}
	};

	const subirPapel = async (c: ContratoFila, file: File | undefined) => {
		if (!file) return;
		setOcupado(c.id);
		try {
			await http.enrollmentRequestsService.subirContratoPapel(campusId, c.id, file);
			avisar("Contrato en papel guardado. La IA lo revisa en un minuto.");
			cargar();
			if (reloj.current) window.clearTimeout(reloj.current);
			reloj.current = window.setTimeout(() => cargarRef.current(), 75000);
		} catch (e) {
			avisar(errorTexto(e), true);
		} finally {
			setOcupado(null);
		}
	};

	const confirmar = async (c: ContratoFila) => {
		setOcupado(c.id);
		try {
			await http.enrollmentRequestsService.confirmarContratoPapel(campusId, c.id);
			avisar("Confirmado como firmado.");
			cargar();
		} catch (e) {
			avisar(errorTexto(e), true);
		} finally {
			setOcupado(null);
		}
	};

	const ver = (c: ContratoFila) => {
		const firmado = esFirmado(c.estado);
		abrirArchivo({ origen: "Contratos", nombre: "Contrato de " + c.alumno + (firmado ? " (firmado)" : ""),
			cargar: () => (c.requestId
				? http.enrollmentRequestsService.downloadContract(campusId, c.requestId, c.id, firmado)
				: http.studentService.downloadStudentDocument(campusId, c.studentId, c.documentoId || 0)).then((r) => r.data) })
			.catch((e) => avisar(errorTexto(e), true));
	};

	const conteo = (k: string): number | null => {
		if (!datos) return null;
		if (k === "") return Object.values(datos.conteos).reduce((a, b) => a + b, 0);
		if (k === "pendientes") return PENDIENTES.reduce((a, e) => a + (datos.conteos[e] || 0), 0);
		if (k === "firmados") return (datos.conteos.firmado || 0) + (datos.conteos.firmado_papel || 0);
		return datos.conteos[k] || 0;
	};

	const acciones = (c: ContratoFila) => {
		const firmado = esFirmado(c.estado);
		const puede = !firmado && c.estado !== "generando";
		return (
			<div className="cfAcciones">
				<button type="button" className="cfBtn" onClick={() => ver(c)}>Ver</button>
				{puede ? <button type="button" className="cfBtn pri" disabled={ocupado === c.id} onClick={() => enviar(c)}>
					{c.estado === "sin_enviar" || c.estado === "pago_pendiente" ? "Enviar" : "Reenviar"}</button> : null}
				{puede ? <button type="button" className="cfBtn" disabled={ocupado === c.id} onClick={() => copiar(c)}>Copiar Enlace</button> : null}
				{c.estado === "revisar" && c.ia !== "pendiente" ? <button type="button" className="cfBtn pri" disabled={ocupado === c.id} onClick={() => confirmar(c)}>Confirmar</button> : null}
				{c.estado !== "firmado" && c.estado !== "generando" ? (
					<label className={"cfBtn" + (ocupado === c.id ? " apagado" : "")} title="Foto o PDF del contrato firmado en papel">
						{c.estado === "firmado_papel" || c.estado === "revisar" ? "Cambiar Papel" : "Subir Papel"}
						<input type="file" accept="image/*,application/pdf" hidden disabled={ocupado === c.id}
							onChange={(e) => { subirPapel(c, e.target.files?.[0]); e.target.value = ""; }} />
					</label>
				) : null}
			</div>
		);
	};

	const paginas = datos ? Math.max(1, Math.ceil(datos.total / perPage)) : 1;

	return (
		<div className="cf">
			<div className="cfFiltros" role="tablist" aria-label="Estado del contrato">
				{FILTROS.map((f) => {
					const n = conteo(f.key);
					return (
						<button key={f.key || "todos"} type="button" role="tab" aria-selected={estado === f.key} className={"cfChip" + (estado === f.key ? " on" : "") + (f.key === "error" && n ? " oro" : "")}
							onClick={() => { setEstado(f.key); setPage(1); }}>
							{f.label}{n !== null ? <span className="cfN">{n.toLocaleString("es-MX")}</span> : null}
						</button>
					);
				})}
			</div>
			<div className="cfBarra">
				<form className="cfBuscar" onSubmit={(e) => { e.preventDefault(); setBuscar(q.trim()); setPage(1); }}>
					<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Alumno, correlativo o # de matrícula" aria-label="Buscar" />
					{buscar ? <button type="button" className="cfBtn" onClick={() => { setQ(""); setBuscar(""); setPage(1); }}>Quitar</button> : null}
				</form>
				<label className="cfFecha"><span>Modo</span>
					<select value={modo} onChange={(e) => { setModo(e.target.value ? Number(e.target.value) : ""); setPage(1); }}>
						<option value="">Todas</option><option value={1}>Digital</option><option value={2}>Presencial</option>
					</select>
				</label>
				{datos && datos.vendedoras.length > 1 ? (
					<label className="cfFecha"><span>Vendió</span>
						<select value={vendio} onChange={(e) => { setVendio(e.target.value ? Number(e.target.value) : ""); setPage(1); }}>
							<option value="">Todas</option>
							{datos.vendedoras.map((v) => <option key={v.id} value={v.id}>{v.nombre}</option>)}
						</select>
					</label>
				) : null}
				<label className="cfFecha"><span>Desde</span><input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPage(1); }} /></label>
				<label className="cfFecha"><span>Hasta</span><input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPage(1); }} /></label>
			</div>
			{aviso ? (
				<div className={"cfAviso" + (aviso.oro ? " oro" : "")} role="status">{aviso.t}
					{aviso.enlace ? <input className="cfEnlace" readOnly value={aviso.enlace} onFocus={(e) => e.target.select()} aria-label="Enlace de firma" /> : null}
				</div>
			) : null}

			{!datos && cargando ? <div className="loader" /> : datos && datos.data.length === 0 ? (
				<div className="cfVacio">{estado === "pendientes" ? "No hay contratos pendientes de firma." : "No hay contratos con este filtro."}</div>
			) : datos ? (
				movil ? (
					<div className="cfTarjetas">
						{datos.data.map((c) => (
							<div className="cfTarjeta" key={c.id}>
								<div className="cfTarjetaCab"><b>{c.alumno}</b><span className={"cfEstado " + tono(c.estado)}>{c.estadoNombre}</span></div>
								<div className="cfSub">{[c.curso, dia(c.fecha), c.modo, c.requestId ? "#" + c.requestId : null].filter(Boolean).join(" · ")}</div>
								{detalle(c) ? <div className={"cfDetalle" + (c.estado === "error" || c.estado === "revisar" ? " oro" : "")}>{detalle(c)}</div> : null}
								{acciones(c)}
							</div>
						))}
					</div>
				) : (
					<table className="cfTabla">
						{/* Esteban, 2026-10-16: el texto hace salto de línea (nada de "…") y los botones van en su propia columna, alineados. */}
						<colgroup><col style={{ width: "18%" }} /><col style={{ width: "12%" }} /><col style={{ width: "7%" }} /><col style={{ width: "13%" }} /><col style={{ width: "20%" }} /><col style={{ width: "30%" }} /></colgroup>
						<thead><tr><th>Alumno</th><th>Curso</th><th>Fecha</th><th>Vendió</th><th>Estado</th><th className="cfAccionesTh">Acciones</th></tr></thead>
						<tbody>
							{datos.data.map((c) => (
								<tr key={c.id}>
									<td><div className="cfLargo"><b>{c.alumno}</b></div><div className="cfSub">{[c.correlativo, c.requestId ? "#" + c.requestId : null].filter(Boolean).join(" · ")}</div></td>
									<td><div className="cfLargo">{c.curso || "—"}</div></td>
									<td>{dia(c.fecha)}</td>
									<td><div className="cfLargo">{c.vendio || "—"}</div>{c.modo ? <div className="cfSub">{c.modo}</div> : null}</td>
									<td>
										<span className={"cfEstado " + tono(c.estado)}>{c.estadoNombre}</span>
										{detalle(c) ? <div className={"cfDetalle cfLargo" + (c.estado === "error" || c.estado === "revisar" ? " oro" : "")}>{detalle(c)}</div> : null}
									</td>
									<td className="cfAccionesTd">{acciones(c)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)
			) : null}

			{datos && paginas > 1 ? (
				<div className="cfPaginas">
					<button type="button" className="cfBtn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</button>
					<span>{page} de {paginas}</span>
					<button type="button" className="cfBtn" disabled={page >= paginas} onClick={() => setPage(page + 1)}>Siguiente</button>
				</div>
			) : null}
		</div>
	);
};

export default ContratosFirma;
